import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Aperture, Lock, Loader2, Upload, Trash2, Check, X, Calendar, Mail, Image as ImageIcon, LogOut, ArrowLeft, Star, SlidersHorizontal, FolderLock } from "lucide-react";
import { api, photoSrc } from "@/lib/api";
import { TestimonialsManager } from "@/components/admin/TestimonialsManager";
import { ServicesManager } from "@/components/admin/ServicesManager";
import { GalleriesManager } from "@/components/admin/GalleriesManager";

const inputCls = "w-full bg-[#0D0D0C] border border-[#F4F0EB]/10 rounded-lg px-4 py-3 font-sub text-sm text-[#F4F0EB] placeholder:text-[#6E695E] focus:border-[#C88D51]/60 focus:outline-none transition-colors";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("lo_token", data.token);
      onLogin(data.user);
    } catch (e) { setErr(e.response?.data?.detail || "Connexion impossible"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0D0D0C] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#161513] border border-[#F4F0EB]/8 rounded-2xl p-8">
        <div className="flex items-center gap-2 mb-2"><Aperture className="w-5 h-5 text-[#C88D51]" /><span className="font-serif-d text-lg">Sel &amp; Lumière</span></div>
        <h1 className="font-serif-d text-2xl mb-1">Espace photographe</h1>
        <p className="font-sub text-sm text-[#6E695E] mb-6 flex items-center gap-2"><Lock className="w-3 h-3" /> Connexion sécurisée</p>
        <div className="space-y-3">
          <input data-testid="admin-login-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          <input data-testid="admin-login-password" type="password" placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        </div>
        {err && <p className="text-[#E4A869] text-sm mt-3 font-sub">{err}</p>}
        <button data-testid="admin-login-submit" disabled={loading} className="btn-accent w-full mt-5 py-3.5 font-sub text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Se connecter
        </button>
        <Link to="/" className="mt-4 inline-flex items-center gap-2 font-sub text-xs text-[#6E695E] hover:text-[#C88D51]"><ArrowLeft className="w-3 h-3" /> Retour au site</Link>
      </form>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [photos, setPhotos] = useState([]);

  const load = async () => {
    const [b, i, p] = await Promise.all([api.get("/bookings"), api.get("/inquiries"), api.get("/photos")]);
    setBookings(b.data); setInquiries(i.data); setPhotos(p.data);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, patch) => { await api.patch(`/bookings/${id}`, patch); load(); toast.success("Réservation mise à jour"); };

  const tabs = [
    { id: "bookings", label: "Réservations", icon: Calendar, n: bookings.filter(b => b.status === "pending").length },
    { id: "inquiries", label: "Demandes", icon: Mail, n: inquiries.length },
    { id: "gallery", label: "Portfolio", icon: ImageIcon, n: photos.length },
    { id: "services", label: "Prestations", icon: SlidersHorizontal },
    { id: "testimonials", label: "Avis", icon: Star },
    { id: "galleries", label: "Galeries privées", icon: FolderLock },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0C]">
      <header className="border-b border-[#F4F0EB]/10 bg-[#141412]">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2"><Aperture className="w-5 h-5 text-[#C88D51]" /><span className="font-serif-d">Tableau de bord</span></div>
          <div className="flex items-center gap-4">
            <span className="font-sub text-sm text-[#A39E93] hidden sm:block">{user.name}</span>
            <button data-testid="admin-logout" onClick={onLogout} className="btn-ghost px-4 py-2 font-sub text-xs flex items-center gap-2"><LogOut className="w-3.5 h-3.5" /> Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex gap-2 mb-8 flex-wrap">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-5 py-2.5 rounded-full font-sub text-sm flex items-center gap-2 transition-colors ${tab === t.id ? "bg-[#C88D51] text-[#0D0D0C]" : "border border-[#F4F0EB]/10 text-[#A39E93] hover:text-[#F4F0EB]"}`}>
              <t.icon className="w-4 h-4" /> {t.label} {t.n !== undefined && <span className="font-mono-a text-xs opacity-70">{t.n}</span>}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="space-y-3">
            {bookings.length === 0 && <p className="font-sub text-[#6E695E]">Aucune réservation pour le moment.</p>}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-[#161513] border border-[#F4F0EB]/8 rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-serif-d text-lg">{b.service_name}</span>
                    <span className={`font-mono-a text-[0.6rem] uppercase px-2 py-0.5 rounded-full ${b.status === "confirmed" ? "bg-[#C88D51]/20 text-[#E4A869]" : b.status === "cancelled" ? "bg-red-500/15 text-red-400" : "bg-[#F4F0EB]/10 text-[#A39E93]"}`}>{b.status}</span>
                    <span className={`font-mono-a text-[0.6rem] uppercase px-2 py-0.5 rounded-full ${b.deposit_status === "paid" ? "bg-green-500/15 text-green-400" : "bg-[#F4F0EB]/5 text-[#6E695E]"}`}>acompte {b.deposit_status}</span>
                  </div>
                  <div className="font-mono-a text-xs text-[#A39E93]">{b.date} · {b.slot} — {b.client_name} · {b.client_email} {b.client_phone && `· ${b.client_phone}`}</div>
                  {b.message && <p className="font-sub text-sm text-[#6E695E] mt-1">{b.message}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`booking-confirm-${b.id}`} onClick={() => setStatus(b.id, { status: "confirmed" })} className="btn-ghost px-3 py-2 text-xs flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Confirmer</button>
                  <button data-testid={`booking-cancel-${b.id}`} onClick={() => setStatus(b.id, { status: "cancelled" })} className="btn-ghost px-3 py-2 text-xs flex items-center gap-1"><X className="w-3.5 h-3.5" /> Annuler</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "inquiries" && (
          <div className="space-y-3">
            {inquiries.length === 0 && <p className="font-sub text-[#6E695E]">Aucune demande pour le moment.</p>}
            {inquiries.map((i) => (
              <div key={i.id} className="bg-[#161513] border border-[#F4F0EB]/8 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-1"><span className="font-serif-d text-lg">{i.name}</span><span className="font-mono-a text-xs text-[#C88D51]">{i.email}</span></div>
                {i.subject && <div className="font-sub text-sm text-[#A39E93]">{i.subject}</div>}
                <p className="font-sub text-sm text-[#6E695E] mt-1">{i.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "gallery" && <GalleryManager photos={photos} reload={load} />}
        {tab === "services" && <ServicesManager />}
        {tab === "testimonials" && <TestimonialsManager />}
        {tab === "galleries" && <GalleriesManager />}
      </div>
    </div>
  );
}

function GalleryManager({ photos, reload }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("plats");
  const [span, setSpan] = useState("standard");
  const [loading, setLoading] = useState(false);

  const upload = async () => {
    if (!file || !title) { toast.error("Choisissez une image et un titre."); return; }
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("title", title); fd.append("category", category); fd.append("span", span);
    try {
      await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo ajoutée à la galerie ✦");
      setFile(null); setTitle(""); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'upload"); }
    setLoading(false);
  };

  const remove = async (id) => { await api.delete(`/photos/${id}`); toast.success("Photo retirée"); reload(); };

  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <div className="bg-[#161513] border border-[#F4F0EB]/8 rounded-xl p-6 h-fit">
        <h3 className="font-serif-d text-xl mb-4">Ajouter une photo</h3>
        <label className="block border border-dashed border-[#F4F0EB]/15 rounded-lg p-6 text-center cursor-pointer hover:border-[#C88D51]/50 transition-colors mb-4">
          <input data-testid="admin-photo-file" type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
          <Upload className="w-6 h-6 text-[#C88D51] mx-auto mb-2" />
          <span className="font-sub text-sm text-[#A39E93]">{file ? file.name : "Cliquez pour choisir une image"}</span>
        </label>
        <input data-testid="admin-photo-title-input" placeholder="Titre de l'œuvre" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls + " mb-3"} />
        <select data-testid="admin-photo-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls + " mb-3"}>
          <option value="plats">Plats</option>
          <option value="restaurants">Restaurants</option>
          <option value="produits">Produits</option>
          <option value="editorial">Éditorial</option>
        </select>
        <select value={span} onChange={(e) => setSpan(e.target.value)} className={inputCls + " mb-4"}>
          <option value="standard">Format standard</option>
          <option value="wide">Format large</option>
          <option value="tall">Format portrait</option>
        </select>
        <button data-testid="admin-photo-submit-btn" onClick={upload} disabled={loading} className="btn-accent w-full py-3 font-sub text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Publier
        </button>
      </div>

      <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((p) => (
          <div key={p.id} className="group relative aspect-square overflow-hidden rounded-lg border border-[#F4F0EB]/8">
            <img src={photoSrc(p)} alt={p.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#0D0D0C]/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
              <span className="font-serif-d text-sm px-2 text-center">{p.title}</span>
              <button data-testid={`admin-photo-delete-${p.id}`} onClick={() => remove(p.id)} className="btn-ghost px-3 py-1.5 text-xs flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Retirer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("lo_token")) { setChecking(false); return; }
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => localStorage.removeItem("lo_token")).finally(() => setChecking(false));
  }, []);

  const logout = () => { localStorage.removeItem("lo_token"); setUser(null); };

  if (checking) return <div className="min-h-screen bg-[#0D0D0C] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C88D51] animate-spin" /></div>;
  if (!user) return <Login onLogin={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
